import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Check, Sparkles, ShieldCheck, Loader2, Radio,
  CheckCircle2, AlertCircle, ExternalLink, CreditCard, Box, AlertOctagon,
  Users, UserPlus, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePlan, refreshPlan } from '../hooks/usePlan';
import { billingApi, usersApi } from '../services/api';
import UpgradePreflightDialog from '../components/billing/UpgradePreflightDialog';
import BindGpsDeviceDialog from '../components/billing/BindGpsDeviceDialog';

const FreeFeatures = [
  'Up to 10 assets',
  'Up to 5 office user seats',
  'Unlimited mechanics',
  'Asset register & QR scanning',
  'Inspections, Defects & Work Orders',
  'Preventative Maintenance scheduling',
  'Compliance & Certificate register',
];

const ProFeatures = [
  'Everything in Free',
  'Unlimited assets',
  { highlight: true, text: 'Guardian AI — chat, work-order assistant & insights' },
  { highlight: true, text: 'Telemetry & GPS — $1.99/asset/month' },
  'Bulk PM scheduling across asset categories',
  'Task Library auto-seeded into work orders',
  'Email + In-app notifications',
  'Branded PDF inspection reports (coming soon)',
  'Priority support',
];

const formatMoneyCents = (cents, currency = 'AUD') =>
  `${currency} $${(cents / 100).toFixed(2)}`;

const PlanCard = ({ name, price, period, features, ctaLabel, ctaTestId, onCta, current, highlight, loading }) => (
  <Card
    className={`relative ${highlight ? 'border-primary/60 shadow-[0_0_0_1px_hsl(var(--primary)/0.3),0_0_30px_hsl(var(--primary)/0.15)]' : 'border-border'}`}
    data-testid={`plan-card-${name.toLowerCase()}`}
  >
    {highlight && (
      <div className="absolute -top-3 left-6">
        <Badge className="bg-primary text-primary-foreground uppercase tracking-widest text-[10px] px-2 py-0.5">
          Recommended
        </Badge>
      </div>
    )}
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-xl">{name}</CardTitle>
        {current && (
          <Badge variant="outline" className="border-amber-500/40 text-amber-400 text-xs" data-testid={`current-plan-${name.toLowerCase()}-badge`}>
            Current
          </Badge>
        )}
      </div>
      <div className="flex items-baseline gap-1 mt-2">
        <span className="text-4xl font-bold font-mono">{price}</span>
        <span className="text-sm text-muted-foreground">/{period}</span>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <ul className="space-y-2.5">
        {features.map((f, i) => {
          const item = typeof f === 'string' ? { text: f } : f;
          return (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${item.highlight ? 'text-primary' : 'text-muted-foreground'}`} />
              <span className={item.highlight ? 'text-foreground font-medium' : 'text-muted-foreground'}>{item.text}</span>
            </li>
          );
        })}
      </ul>
      <Button
        className="w-full"
        variant={current ? 'outline' : (highlight ? 'default' : 'outline')}
        onClick={onCta}
        disabled={current || loading}
        data-testid={ctaTestId}
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        {ctaLabel}
      </Button>
    </CardContent>
  </Card>
);

const AssetUsageCard = ({ assetCount, assetLimit, isPro }) => {
  if (isPro) return null; // unlimited on Pro
  const pct = assetLimit ? Math.min(100, (assetCount / assetLimit) * 100) : 0;
  const atCap = assetCount >= (assetLimit || 0);
  const nearCap = !atCap && pct >= 80;
  return (
    <Card data-testid="asset-usage-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Box className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Asset register usage</CardTitle>
              <CardDescription className="text-xs">
                Free plan is capped at <strong className="text-foreground">{assetLimit}</strong> assets. Upgrade to Pro for unlimited.
              </CardDescription>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Used</div>
            <div
              className={`text-xl font-mono font-semibold ${atCap ? 'text-red-400' : nearCap ? 'text-amber-400' : 'text-foreground'}`}
              data-testid="asset-usage-count"
            >
              {assetCount} / {assetLimit}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full transition-all ${atCap ? 'bg-red-500' : nearCap ? 'bg-amber-400' : 'bg-muted-foreground/50'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {atCap && (
          <p className="text-sm text-red-400 mt-3" data-testid="asset-cap-notice">
            You've reached the {assetLimit}-asset cap. Upgrade to Pro to add more.
          </p>
        )}
        {nearCap && (
          <p className="text-sm text-amber-400 mt-3" data-testid="asset-near-cap-notice">
            You're approaching the {assetLimit}-asset cap ({assetLimit - assetCount} remaining).
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const OfficeUsersCard = ({ onInviteClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    usersApi.getSeatUsage()
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const seatsUsed = data?.used ?? 0;
  const seatsIncluded = data?.office_seats_included ?? 0;
  const billableSeats = data?.billable_seats ?? Math.max(0, seatsUsed - seatsIncluded);
  const seatPriceCents = data?.seat_price_cents ?? 0;
  const projectedCents = data?.projected_monthly_charge_cents ?? billableSeats * seatPriceCents;
  const currency = data?.currency || 'AUD';

  return (
    <Card data-testid="office-users-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Office user seats</CardTitle>
              <CardDescription className="text-xs">
                Admin & Fleet Manager seats — billed at{' '}
                <strong className="text-foreground">{formatMoneyCents(seatPriceCents, currency)}</strong>{' '}
                per seat per month. Mechanics and mobile field users are unlimited and free.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onInviteClick}
              className="border-[#FFD108]/40 text-[#FFD108] hover:bg-[#FFD108]/10"
              data-testid="billing-invite-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              Invite a user
            </Button>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Projected this month
              </div>
              <div className="text-xl font-mono font-semibold" data-testid="office-seats-projected">
                {loading ? '—' : formatMoneyCents(projectedCents, currency)}
              </div>
              <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                {loading ? '' : (
                  <span>
                    {seatsUsed} {seatsUsed === 1 ? 'seat' : 'seats'}
                    {seatsIncluded > 0 && <span> · {seatsIncluded} included free</span>}
                    {billableSeats > 0 && (
                      <span> · {billableSeats} × {formatMoneyCents(seatPriceCents, currency)}/mo</span>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

const TelematicsUsageCard = ({ gpsCount, gpsPriceCents, currency, isPro, subStatus, onBindClick }) => {
  const monthly = (gpsCount || 0) * (gpsPriceCents || 0);
  return (
    <Card data-testid="telematics-usage-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Radio className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">GPS Telemetry add-on</CardTitle>
              <CardDescription className="text-xs">
                Pro feature · <strong className="text-foreground">{formatMoneyCents(gpsPriceCents, currency)}</strong> per asset per month.
                Billed automatically as you enable GPS on assets.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isPro && (
              <Button
                variant="outline"
                size="sm"
                onClick={onBindClick}
                className="border-[#FFD108]/40 text-[#FFD108] hover:bg-[#FFD108]/10"
                data-testid="bind-gps-device-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Bind GPS device
              </Button>
            )}
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Projected this month</div>
              <div className="text-xl font-mono font-semibold" data-testid="gps-projected-charge">
                {formatMoneyCents(monthly, currency)}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="font-mono text-muted-foreground">
            <span className="text-foreground font-semibold" data-testid="gps-active-count">{gpsCount}</span>{' '}
            GPS-enabled asset{gpsCount === 1 ? '' : 's'}
          </span>
          {!isPro && (
            <Badge variant="outline" className="border-amber-500/40 text-amber-400 text-[10px]">
              Pro only
            </Badge>
          )}
          {isPro && subStatus && (
            <Badge variant="outline" className="text-[10px]" data-testid="sub-status-badge">
              {subStatus}
            </Badge>
          )}
        </div>
        {!isPro && (
          <p className="text-sm text-muted-foreground">
            Telemetry is unavailable on the Free plan. Upgrade to Pro to wire up your fleet's GPS.
          </p>
        )}
        {isPro && gpsCount === 0 && (
          <p className="text-sm text-muted-foreground">
            No GPS hardware enabled yet — bind an IMEI to an asset on the Telemetry tab to start tracking.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const BillingPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isPro } = usePlan();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // 'checkout' | 'portal'
  const [statusBanner, setStatusBanner] = useState(null);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [bindGpsOpen, setBindGpsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingApi.me();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load billing details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe redirect back: ?checkout=success&session_id=... or ?checkout=cancelled
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    if (!checkout) return;
    if (checkout === 'cancelled') {
      setStatusBanner({ type: 'warn', text: 'Checkout was cancelled — your plan is unchanged.' });
      setSearchParams({}, { replace: true });
      return;
    }
    if (checkout === 'success' && sessionId) {
      setStatusBanner({ type: 'pending', text: 'Confirming payment — this can take a few seconds…' });
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const res = await billingApi.checkoutStatus(sessionId);
          if (res.data?.payment_status === 'paid') {
            setStatusBanner({ type: 'success', text: 'Payment successful — welcome to Pro!' });
            await refreshPlan();
            await load();
            setSearchParams({}, { replace: true });
            return;
          }
        } catch (e) { /* swallow; retry */ }
        if (attempts < 8) setTimeout(poll, 2000);
        else {
          setStatusBanner({ type: 'warn', text: "We couldn't confirm payment yet. Refresh the page in a minute or contact support." });
          setSearchParams({}, { replace: true });
        }
      };
      poll();
    }
  }, [searchParams, setSearchParams, load]);

  const handleUpgrade = async () => {
    // Open the preflight dialog instead of jumping straight to Stripe.
    // The dialog captures office-user + GPS-device intent and calls
    // `handleUpgradeContinue` with the answers.
    setPreflightOpen(true);
  };

  const handleUpgradeContinue = async ({ officeUsers, gpsDevices }) => {
    setActionLoading('checkout');
    try {
      const res = await billingApi.checkout(window.location.origin, {
        officeUsers,
        gpsDevices,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('No checkout URL returned');
        setActionLoading(null);
        setPreflightOpen(false);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Checkout failed');
      setActionLoading(null);
      setPreflightOpen(false);
    }
  };

  const handlePortal = async () => {
    setActionLoading('portal');
    try {
      const res = await billingApi.portal(window.location.origin);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('No portal URL returned');
        setActionLoading(null);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Portal session failed');
      setActionLoading(null);
    }
  };

  const sub = data?.subscription || {};

  return (
    <div className={embedded ? "space-y-8" : "space-y-8 max-w-6xl mx-auto p-6 lg:p-8"} data-testid="billing-page">
      {!embedded && (
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <button
              onClick={() => navigate('/settings')}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-settings-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Settings
            </button>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Billing & Plan</h1>
            <p className="text-base text-muted-foreground max-w-2xl">
              Manage your EQUIPR Guardian subscription. Pro unlocks Guardian AI, unlimited assets,
              and the per-asset GPS telemetry add-on.
            </p>
          </div>
        </div>
      )}

      {statusBanner && (
        <Card
          className={`border ${
            statusBanner.type === 'success' ? 'border-emerald-500/40 bg-emerald-500/5' :
            statusBanner.type === 'warn'    ? 'border-amber-500/40 bg-amber-500/5' :
                                              'border-primary/40 bg-primary/5'
          }`}
          data-testid="status-banner"
        >
          <CardContent className="py-4 flex items-center gap-3">
            {statusBanner.type === 'success' && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
            {statusBanner.type === 'warn'    && <AlertCircle className="w-5 h-5 text-amber-400" />}
            {statusBanner.type === 'pending' && <Loader2 className="w-5 h-5 text-primary animate-spin" />}
            <p className="text-sm">{statusBanner.text}</p>
          </CardContent>
        </Card>
      )}

      {/* Failed-payment banner — Stripe couldn't charge the card on file */}
      {data?.failed_payment && (
        <Card className="border border-red-500/50 bg-red-500/5" data-testid="failed-payment-banner">
          <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <AlertOctagon className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-200">
                  Payment failed — {data.failed_payment.amount_due_display}
                </p>
                <p className="text-xs text-red-100/70 mt-0.5">
                  Stripe couldn't charge your card on file (attempt #{data.failed_payment.attempt_count}).
                  {data.failed_payment.next_attempt_at && (
                    <> Next retry: {new Date(data.failed_payment.next_attempt_at).toLocaleString()}.</>
                  )}
                  {' '}Update your card or pay the invoice to restore Pro features.
                </p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {data.failed_payment.hosted_invoice_url && (
                <Button
                  asChild
                  variant="outline"
                  className="border-red-500/50 text-red-200 hover:bg-red-500/10"
                  data-testid="failed-payment-invoice-btn"
                >
                  <a href={data.failed_payment.hosted_invoice_url} target="_blank" rel="noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Pay invoice
                  </a>
                </Button>
              )}
              <Button
                onClick={handlePortal}
                className="btn-industrial"
                disabled={actionLoading === 'portal'}
                data-testid="failed-payment-portal-btn"
              >
                {actionLoading === 'portal' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Update card
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && data && (
        <Card data-testid="current-plan-summary">
          <CardContent className="py-6 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="label-overline text-xs uppercase tracking-widest text-muted-foreground">Current plan</p>
                <p className="text-xl font-semibold capitalize" data-testid="current-plan-name">{data.plan}</p>
                {sub.current_period_end && data.is_pro && (
                  <p className="text-xs text-muted-foreground" data-testid="current-period-end">
                    Renews {new Date(sub.current_period_end).toLocaleDateString()}
                    {sub.cancel_at_period_end ? ' · cancels at period end' : ''}
                  </p>
                )}
              </div>
            </div>
            {data.is_pro ? (
              <Button
                onClick={handlePortal}
                variant="outline"
                disabled={actionLoading === 'portal'}
                data-testid="manage-subscription-btn"
              >
                {actionLoading === 'portal' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Manage subscription
                <ExternalLink className="w-3 h-3 ml-2" />
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="w-4 h-4 text-primary" />
                <span>Guardian AI & GPS telemetry are unlocked on Pro.</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PlanCard
          name="Free"
          price="$0"
          period="month"
          features={FreeFeatures}
          ctaLabel={!isPro ? 'Your current plan' : 'Downgrade via portal'}
          ctaTestId="plan-free-cta"
          current={!isPro}
          highlight={false}
          loading={actionLoading === 'portal'}
          onCta={isPro ? handlePortal : undefined}
        />
        <PlanCard
          name="Pro"
          price={
            data?.pro_price
              ? formatMoneyCents(data.pro_price.amount_cents, data.pro_price.currency)
              : '—'
          }
          period={data?.pro_price?.interval || 'month'}
          features={ProFeatures}
          ctaLabel={isPro ? 'Your current plan' : 'Upgrade to Pro'}
          ctaTestId="plan-pro-cta"
          current={isPro}
          highlight={!isPro}
          loading={actionLoading === 'checkout'}
          onCta={isPro ? undefined : handleUpgrade}
        />
      </div>

      {data && (
        <>
          <AssetUsageCard
            assetCount={data.asset_count}
            assetLimit={data.asset_limit}
            isPro={data.is_pro}
          />
          <TelematicsUsageCard
            gpsCount={data.gps_count}
            gpsPriceCents={data.gps_price_cents}
            currency={data.currency}
            isPro={data.is_pro}
            subStatus={sub.status}
            onBindClick={() => setBindGpsOpen(true)}
          />
          {data.is_pro && (
            <OfficeUsersCard onInviteClick={() => navigate('/users?invite=1')} />
          )}
        </>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading plan…
        </div>
      )}

      <UpgradePreflightDialog
        open={preflightOpen}
        onOpenChange={setPreflightOpen}
        onContinue={handleUpgradeContinue}
        loading={actionLoading === 'checkout'}
        proPriceCents={data?.pro_price?.unit_amount || 0}
        gpsPriceCents={data?.gps_price_cents || 0}
        currency={data?.currency || 'AUD'}
      />

      <BindGpsDeviceDialog
        open={bindGpsOpen}
        onOpenChange={setBindGpsOpen}
        onBound={load}
      />
    </div>
  );
};

export default BillingPage;
